import React, { useContext, useMemo } from 'react';
import get from 'lodash/get';
import { produce } from 'immer';
import set from 'lodash/set';
import values from 'lodash/values';

import {
  DEFAULT_VOICE,
  SUPPORTED_VOICES,
  VOICE_LANGUAGES_MAPPER,
  VOICES_OPTIONS,
} from 'src/presentation/components/pages/ivr/constants/ivrLanguageConstants';
import Dropdown from 'src/presentation/components/forms/Dropdown';
import ExternalLink from 'src/presentation/components/pages/shared/ExternalLink';
import FormContext from 'src/presentation/contexts/FormContext';
import Input from 'src/presentation/components/forms/Input';
import { InputField } from 'src/presentation/components/agent_desktop_components';
import { Row } from 'src/presentation/components/pages/ivr/layouts/IvrLayout';

type VoiceSelectorType = {
  attr: string;
  disabled?: boolean;
  readOnly?: boolean;
  value?: string;
};

const VoiceSelector = ({ attr, disabled, readOnly }: VoiceSelectorType) => {
  const { workingState } = useContext(FormContext);
  const voiceAttr = `${attr}.message.voice`;
  const selectedVoice = useMemo(() => getVoiceDropdownValue({ workingState, voiceAttr }), [workingState, voiceAttr]);

  const isSupportedVoice = !selectedVoice || values(SUPPORTED_VOICES).includes(selectedVoice as any);

  const { languageOptions, defaultLanguage } = get(
    VOICE_LANGUAGES_MAPPER,
    selectedVoice,
    VOICE_LANGUAGES_MAPPER[DEFAULT_VOICE]
  );

  const languageAttr = `${attr}.message.language`;
  const selectedLanguage = get(workingState, languageAttr) || defaultLanguage.value;

  const voiceSelector = isSupportedVoice ? (
    <VoiceControl attr={attr} voiceAttr={voiceAttr} disabled={disabled} readOnly={readOnly} />
  ) : (
    <Input attr={voiceAttr} data-aid={voiceAttr} label="Voice" readOnly />
  );

  // TODO Add a list of supported third-parties voices (e.g. 'Polly.Joanna') and the languages they support
  const languageSelector = isSupportedVoice && (
    <Row>
      <Dropdown
        attr={languageAttr}
        data-aid="message.language"
        description="The language we will speak the above text in"
        label="Language"
        options={languageOptions}
        readOnly={readOnly || !isSupportedVoice}
        disabled={disabled || !isSupportedVoice}
        placeholder={defaultLanguage.label}
        value={selectedLanguage}
      />
    </Row>
  );

  return (
    <>
      {voiceSelector}
      {languageSelector}
    </>
  );
};

const getVoiceDropdownValue = ({ workingState, voiceAttr }) => {
  let selectedVoice = get(workingState, voiceAttr, DEFAULT_VOICE) as string;

  if (
    !([SUPPORTED_VOICES.MAN_VOICE, SUPPORTED_VOICES.WOMAN_VOICE, SUPPORTED_VOICES.ALICE_VOICE] as string[]).includes(
      selectedVoice
    )
  ) {
    selectedVoice = SUPPORTED_VOICES.CUSTOM;
  }
  return selectedVoice;
};

const VoiceControl = ({ attr, voiceAttr, disabled, readOnly }) => {
  const { workingState, updateState, onBlur } = useContext(FormContext);

  const selectedVoice = useMemo(() => getVoiceDropdownValue({ workingState, voiceAttr }), [workingState, voiceAttr]);

  const onVoiceChange = voice => {
    const updatedState = handleInputChange(voice);
    onBlur && onBlur({ currentState: updatedState });
  };

  const handleInputChange = voice => {
    // sets voice and resets the language to prevent it from being stored
    // from an incompatible list of languages of another voice
    const updatedState = produce(workingState, draft => {
      set(draft, `${attr}.message.voice`, voice);
      set(draft, `${attr}.message.language`, '');
    });
    updateState(updatedState);
    return updatedState;
  };

  return (
    <>
      <Row>
        <Dropdown
          attr={voiceAttr}
          data-aid="message.voice"
          disabled={disabled}
          label="Voice"
          onChange={onVoiceChange}
          options={VOICES_OPTIONS}
          readOnly={readOnly}
          value={selectedVoice}
        />
      </Row>
      {selectedVoice === SUPPORTED_VOICES.CUSTOM && (
        <Row>
          <InputField
            attr={voiceAttr}
            data-aid="message.voice"
            label="Custom Voice"
            description={
              <>
                Supported voices are documented{' '}
                <ExternalLink
                  label="here"
                  to="https://www.twilio.com/docs/voice/twiml/say/text-speech#available-voices-and-languages"
                />
                {' and '}
                <ExternalLink
                  label="here"
                  to="https://www.twilio.com/docs/voice/twiml/say/text-speech#available-voices-and-languages"
                />{' '}
                and are subject to additional telephony fees
              </>
            }
            readOnly={readOnly}
            disabled={disabled}
            onChange={handleInputChange}
            value={get(workingState, voiceAttr, '')}
            onBlur={onBlur}
          />
        </Row>
      )}
    </>
  );
};

export default VoiceSelector;
